import {Fragment, h} from "preact";
import Input from "../../Input";
import s from "./beneficiaryForm.module.css";
import {useBeneficiaryForm} from "./useBeneficiaryForm";
import { useAppContext } from "../../../contexts";
import ForeignerUserFields from "./ForeignerUserFields";
import GenderField from "./GenderField";
import ParentalFields from "./ParentalFields";


const BeneficiaryForm = props => {
    // Destructured props
    const {
        beneficiary,
        setOpen,
        rehydrateBeneficiariesState,
        handleBeneficiary,
        stockItem,
        handleCurrentBeneficiaryChange,
        enableForeigner,
        source,
        nadOrder,
        productId = undefined
    } = props;

    // Component logic data
    const componentProps = useBeneficiaryForm({
        beneficiary,
        setOpen,
        rehydrateBeneficiariesState,
        handleBeneficiary,
        stockItem,
        handleCurrentBeneficiaryChange,
        source,
        nadOrder,
        productId
    });

    // Destructured component logic data
    const {
        formValues,
        errors,
        isLoading,
        isUnderEighteen,
        submitError,
        handleChange,
        handleSubmit
    } = componentProps;

    // Accessibility feature
    const {highContrast} = useAppContext();

    // Variable to store the brazilian check fieldset
    const brazilianCheck = enableForeigner ? (
        <fieldset className={"flex flex-row items-center justify-start gap-x-2"}>
            <input
                type={"checkbox"}
                id={"beneficiary-nationality-check-input"}
                name={"isBrazilian"}
                className={s.isBrazilianInput}
                checked={formValues.isBrazilian}
                onInput={handleChange}
            />
            <label
                htmlFor={"beneficiary-nationality-check-input"}
                className={`${s.nationalityCheckInputLabel} ${highContrast ? "highLabelFourth" : ""}`}
            >
                É brasileiro?
            </label>
        </fieldset>
    ) : null;

    // Label to be displayed at the submit button
    const buttonLabel =
        Object.keys(beneficiary).length > 1 ?
            'EDITAR BENEFICIÁRIO' :
            'CADASTRAR BENEFICIÁRIO';

    // In case the beneficiary is under eighteen years old
    const parentalFields = !isUnderEighteen ? null : (
        <ParentalFields
            formValues={formValues}
            errors={errors}
            handleChange={handleChange}
        />
    );

    // Variable to store both the brazilian and foreigner fields, already being correctly conditioned
    const conditionalFields = !formValues.isBrazilian ? (
        <ForeignerUserFields
            formValues={formValues}
            errors={errors}
            handleChange={handleChange}
        />
    ) : null;

    // Checkbox switcher for the social name field
    const socialNameSwitch = (
        <fieldset className={"flex flex-row items-center justify-start gap-x-2"}>
            <input
                type={"checkbox"}
                id={"social-name-switch-input"}
                name={"includeSocialName"}
                className={s.isBrazilianInput}
                checked={formValues.includeSocialName}
                onInput={handleChange}
            />
            <label
                htmlFor={"social-name-switch-input"}
                className={`
                    ${s.nationalityCheckInputLabel} 
                    ${highContrast ? "highLabelFourth" : ""}
                `}
            >
                Incluir nome social
            </label>
        </fieldset>
    );

    // The variable name already says it all
    const socialNameField =
        !formValues.includeSocialName ? null : (
            <Input
                type={"text"}
                label={"Nome social:"}
                name={"socialName"}
                handleChange={handleChange}
                placeholder={"Digite aqui o nome social"}
                value={formValues.socialName}
                error={errors.socialName}
                isRequired={false}
            />
        ) ;

    return (
        <Fragment>
            <form
                onSubmit={handleSubmit}
                className={`flex flex-col items-start justify-between gap-y-6 ${s.fadeAnimation}`}
            >
                {brazilianCheck}
                <Input
                    type={"text"}
                    label={"Nome completo:"}
                    name={"name"}
                    handleChange={handleChange}
                    placeholder={"Insira o nome do beneficiário"}
                    value={formValues.name}
                    error={errors.name}
                    isRequired={true}
                />
                <div className={"w-full flex flex-col items-start justify-between gap-y-6"}>
                    {socialNameSwitch}
                    {socialNameField}
                </div>
                <div className={'w-full flex flex-col md:flex-row justify-between gap-[1.5rem]'}>
                    <Input
                        type={"text"}
                        label={"Data de nascimento:"}
                        name={"birthdate"}
                        placeholder={"DD/MM/AAAA"}
                        handleChange={handleChange}
                        value={formValues.birthdate}
                        error={errors.birthdate}
                        isRequired={true}
                        maxlength={10}
                    />
                    <GenderField
                        formValues={formValues}
                        errors={errors}
                        handleChange={handleChange}
                    />
                </div>
                <div className={'w-full flex flex-col md:flex-row justify-between gap-[1.5rem]'}>
                    {formValues.isBrazilian ? (
                        <Fragment>
                            {!isUnderEighteen ? (
                                <Input
                                    type={"text"}
                                    label={"CPF"}
                                    name={"cpf"}
                                    placeholder={"CPF do beneficiário"}
                                    handleChange={handleChange}
                                    value={formValues.cpf}
                                    error={errors.cpf}
                                    isRequired={true}
                                />
                            ) : (
                                <Input
                                    type={"text"}
                                    label={"CPF do responsável:"}
                                    name={"responsibleTaxVat"}
                                    placeholder={"Digite o CPF do responsável"}
                                    handleChange={handleChange}
                                    value={formValues.responsibleTaxVat}
                                    error={errors.responsibleTaxVat}
                                    isRequired={true}
                                />
                            )}
                        </Fragment>
                    ) : (
                        <Fragment>
                            {!isUnderEighteen ? (
                                <Input
                                    type={"text"}
                                    label={"Passaporte:"}
                                    name={"passport"}
                                    placeholder={"Digite o passaporte do beneficiário"}
                                    handleChange={handleChange}
                                    value={formValues.passport}
                                    error={errors.passport}
                                    isRequired={true}
                                />
                            ) : (
                                <Input
                                    type={"text"}
                                    label={"Passaporte do Responsável:"}
                                    name={"responsiblePassport"}
                                    placeholder={"Digite o passaporte do responsável"}
                                    handleChange={handleChange}
                                    value={formValues.responsiblePassport}
                                    error={errors.responsiblePassport}
                                    isRequired={true}
                                />
                            )}
                        </Fragment>
                    )}
                    {!isUnderEighteen ? (
                        <Input
                            type={"text"}
                            label={"e-mail:"}
                            name={"email"}
                            handleChange={handleChange}
                            placeholder={"E-mail do beneficiário"}
                            value={formValues.email}
                            error={errors.email}
                            isRequired={true}
                        />
                    ) : (
                        <Input
                            type={"email"}
                            label={"Email do responsável:"}
                            name={"responsibleEmail"}
                            placeholder={"Digite o e-mail do responsável"}
                            handleChange={handleChange}
                            value={formValues.responsibleEmail}
                            error={errors.responsibleEmail}
                            isRequired={true}
                        />
                    )}
                </div>
                {conditionalFields}
                {parentalFields}
                <Input
                    type={"text"}
                    label={"Filiação:"}
                    subLabel={"(mãe, pai ou responsável no registro de nascimento)"}
                    name={"affiliation"}
                    placeholder={"Mãe, pai ou responsável no registro de nascimento"}
                    handleChange={handleChange}
                    value={formValues.affiliation}
                    error={errors.affiliation}
                    isRequired={true}
                />
                {!!submitError && (
                    <p className={s.errorMessage}>
                        {submitError}
                    </p>
                )}
                <div className="mt-5 flex gap-[1rem]">
                    <button
                        type="submit"
                        className={`inline-flex w-full justify-center items-center rounded-md bg-light-green px-3 py-2 text-sm text-white shadow-sm ring-1 ring-inset ring-light-green hover:bg-dark-green sm:col-start-1 sm:mt-0 select-none ${(isLoading ? "opacity-50" : "")} ${highContrast ? "highFormButtonSecond !ring-0" : ""}`}
                        disabled={isLoading}
                    >
                        {buttonLabel}
                    </button>
                </div>
            </form>
        </Fragment>
    )
}

export default BeneficiaryForm;
